import * as React from 'react';
import { Activity, Save, AlertTriangle, Plus, Trash2, Zap, CheckCircle, ShieldAlert, Info, Edit, X } from 'lucide-react';
import {
  NCSTestResult,
  NCSTestReferenceValues,
  ReferenceValueRange,
  SpecialStudyTest,
  SpecialStudyData,
  NCSStudyMode,
  NCSSensoryTechnique,
  StimulationSite,
  DistanceUnit,
} from '../types/ncs';
import { nerveDatabase } from '../data/nerveData';
import {
  specialStudiesDatabase,
  specialStudiesCategories,
  SpecialStudyDefinition,
} from '../data/specialStudiesDatabase';
import { PhysiologicalValidationService, ValidationAlert } from '../services/physiologicalValidationService';

// Extended NerveData interface for internal use
interface ExtendedNerveData {
  id: string;
  name: string;
  category: 'motor' | 'sensory';
  referenceValues: {
    latency: ReferenceValueRange;
    amplitude: ReferenceValueRange;
    velocity: ReferenceValueRange;
  };
  defaultAmplitudeUnit: 'mV' | 'µV';
}

// Helper function to get extended nerve data
const getExtendedNerveData = (id: string): ExtendedNerveData | undefined => {
  const baseNerve = nerveDatabase.find(n => n.id === id);
  if (!baseNerve) return undefined;

  // Determine category based on ID patterns
  const category: 'motor' | 'sensory' = 
    id.includes('sensory') || 
    id === 'sural' || 
    id === 'sural_sensory' ||
    id === 'superficial_peroneal' || 
    id === 'peroneal_superficial_sensory' ||
    id === 'lateral_femoral_cutaneous' || 
    id === 'saphenous' ||
    id === 'saphenous_sensory'
      ? 'sensory' 
      : 'motor';

  // Determine default amplitude unit based on category
  const defaultAmplitudeUnit: 'mV' | 'µV' = category === 'sensory' ? 'µV' : 'mV';

  return {
    id: baseNerve.id,
    name: baseNerve.name,
    category,
    referenceValues: baseNerve.referenceValues,
    defaultAmplitudeUnit
  };
};

// Helper functions to filter nerves by category and region
const getMotorNerves = (region: 'upper' | 'lower' | 'all' = 'all') => {
  return nerveDatabase.filter(nerve => {
    const isMotor = nerve.id.includes('motor') || 
                   nerve.id === 'peroneal' || 
                   nerve.id === 'tibial' || 
                   nerve.id === 'femoral' || 
                   nerve.id === 'sciatic' || 
                   nerve.id === 'axillary' || 
                   nerve.id === 'musculocutaneous' || 
                   nerve.id === 'suprascapular' || 
                   nerve.id === 'accessory' ||
                   nerve.id === 'facial_motor';
    
    if (!isMotor) return false;
    
    if (region === 'upper') {
      return nerve.id.includes('median') || nerve.id.includes('ulnar') || nerve.id.includes('radial') ||
             nerve.id === 'axillary' || nerve.id === 'musculocutaneous' || nerve.id === 'suprascapular' ||
             nerve.id === 'accessory' || nerve.id === 'facial_motor';
    } else if (region === 'lower') {
      return nerve.id.includes('peroneal') || nerve.id.includes('tibial') || 
             nerve.id === 'femoral' || nerve.id === 'sciatic';
    }
    return true;
  });
};

const getSensoryNerves = (region: 'upper' | 'lower' | 'all' = 'all') => {
  return nerveDatabase.filter(nerve => {
    const isSensory = nerve.id.includes('sensory') || 
                     nerve.id === 'sural' || 
                     nerve.id === 'sural_sensory' ||
                     nerve.id === 'superficial_peroneal' || 
                     nerve.id === 'peroneal_superficial_sensory' ||
                     nerve.id === 'lateral_femoral_cutaneous' || 
                     nerve.id === 'saphenous' ||
                     nerve.id === 'saphenous_sensory' ||
                     nerve.id === 'median_ulnar_mixed_palm_wrist';
    
    if (!isSensory) return false;
    
    if (region === 'upper') {
      return nerve.id.includes('median') || nerve.id.includes('ulnar') || nerve.id.includes('radial');
    } else if (region === 'lower') {
      return nerve.id.includes('sural') || nerve.id.includes('peroneal_superficial') || 
             nerve.id.includes('superficial_peroneal') || nerve.id.includes('saphenous') || 
             nerve.id === 'lateral_femoral_cutaneous';
    }
    return true;
  });
};



const martinGruberStudy: SpecialStudyDefinition = {
  id: 'martin_gruber_crossover',
  name: 'Anastomosis Martin-Gruber (crossover mediano-cubital, PAMC mediano codo -> ADM)',
  category: 'conduction_block',
  type: 'other',
  description: 'Confirma anastomosis mediano-cubital para evitar interpretar pseudo-bloqueo como bloqueo desmielinizante.',
  clinicalIndications: ['Caída de amplitud cubital en antebrazo', 'Sospecha de pseudo-bloqueo por Martin-Gruber'],
  parameters: ['pamcMedianStimElbowAdm'],
  referenceValues: { pamcMedianStimElbowAdm: { min: 0, max: 25 } },
  units: { pamcMedianStimElbowAdm: 'mV' },
  sideOptions: ['left', 'right', 'bilateral'],
  diagnosticValue: 'high',
  technicalComplexity: 'intermediate',
};

const availableSpecialStudies: SpecialStudyDefinition[] = [
  ...specialStudiesDatabase,
  martinGruberStudy,
];

const toSpecialStudyTestType = (
  type: SpecialStudyDefinition['type']
): SpecialStudyTest['type'] => {
  if (type === 'h_reflex' || type === 'blink_reflex' || type === 'f_wave' || type === 'rns') {
    return type;
  }
  return 'other';
};

const formatReportedDistance = (value: number, unit?: DistanceUnit): string =>
  `${value}${unit || 'cm'}`;

const stimulationSiteOptions: Array<{ value: StimulationSite; label: string }> = [
  { value: 'wrist', label: 'Muñeca' },
  { value: 'elbow', label: 'Codo' },
  { value: 'below_elbow', label: 'Debajo del codo' },
  { value: 'above_elbow', label: 'Encima del codo' },
  { value: 'axilla', label: 'Axila' },
  { value: 'erb_point', label: 'Punto de Erb' },
  { value: 'ankle', label: 'Tobillo' },
  { value: 'below_fibular_head', label: 'Debajo cabeza peroné' },
  { value: 'above_fibular_head', label: 'Encima cabeza peroné' },
  { value: 'popliteal_fossa', label: 'Fosa poplítea' },
  { value: 'other', label: 'Otro' },
];

type ScreeningPresetId = 'carpal_tunnel' | 'polyneuropathy' | 's1_radiculopathy' | 'nmj';

const screeningPresets: Array<{
  id: ScreeningPresetId;
  label: string;
  description: string;
  nerveIds: string[];
  specialStudyIds?: string[];
}> = [
  {
    id: 'carpal_tunnel',
    label: 'Túnel Carpiano',
    description: 'Mediano M/S, cubital M/S y comparativas palma-muñeca.',
    nerveIds: ['median_motor', 'median_sensory', 'median_sensory_palm', 'ulnar_motor', 'ulnar_sensory', 'median_ulnar_mixed_palm_wrist'],
  },
  {
    id: 'polyneuropathy',
    label: 'Polineuropatía',
    description: 'Screening distal sensitivo-motor en miembros inferiores y comparadores superiores.',
    nerveIds: ['sural_sensory', 'peroneal_superficial_sensory', 'tibial_motor', 'peroneal_motor', 'median_sensory', 'ulnar_sensory'],
  },
  {
    id: 's1_radiculopathy',
    label: 'Radiculopatía S1',
    description: 'Tibial/peroneo, sural y reflejo H; recordar EMG paravertebral.',
    nerveIds: ['tibial_motor', 'peroneal_motor', 'sural_sensory'],
    specialStudyIds: ['h_reflex_soleus'],
  },
  {
    id: 'nmj',
    label: 'Unión Neuromuscular',
    description: 'Facial/accesorio/ulnar y estimulación repetitiva.',
    nerveIds: ['facial_motor', 'accessory', 'ulnar_motor'],
    specialStudyIds: ['rns_trapezius_3hz', 'rns_anconeus_50hz'],
  },
];

interface NeuroConductionFormProps {
  onSave: (data: { ncsResults: NCSTestResult[], specialStudies: SpecialStudyData }) => void;
  initialData?: {
    ncsResults?: NCSTestResult[];
    specialStudies?: SpecialStudyData;
  };
  /** Notifica borrador NCS + especiales (p. ej. alertas Martin-Gruber en el flujo clínico). */
  onDraftChange?: (draft: { ncsResults: NCSTestResult[]; specialStudies: SpecialStudyData }) => void;
}

// Current NCS input state
interface CurrentNCSInput {
  nerveId: string;
  side: 'left' | 'right';
  studyMode: NCSStudyMode;
  sensoryTechnique: NCSSensoryTechnique;
  stimulationSite: StimulationSite | '';
  recordingSite: string;
  distance: number | string;
  distanceUnit: DistanceUnit;
  temperature: number | string;
  filterLowHz: number | string;
  filterHighHz: number | string;
  averagingCount: number | string;
  latency: number | string;
  peakLatency?: number | string;
  amplitude: number | string;
  velocity: number | string;
  duration?: number | string;
  area?: number | string;
  proximalLatency?: number | string;
  proximalAmplitude?: number | string;
  proximalDuration?: number | string;
  proximalArea?: number | string;
  proximalStimulationSite?: StimulationSite | '';
  segmentDistance?: number | string;
  segmentDistanceUnit: DistanceUnit;
  segmentName?: string;
  notes?: string;
}

// Current Special Study input state
interface CurrentSpecialStudyInput {
  studyId: string;
  side: 'left' | 'right' | 'bilateral';
  values: Record<string, number | string>;
  notes?: string;
}

const initialCurrentInputState: CurrentNCSInput = {
  nerveId: '',
  side: 'right',
  studyMode: 'single_site',
  sensoryTechnique: 'antidromic',
  stimulationSite: '',
  recordingSite: '',
  distance: '',
  distanceUnit: 'cm',
  temperature: '',
  filterLowHz: '',
  filterHighHz: '',
  averagingCount: '',
  latency: '',
  peakLatency: '',
  amplitude: '',
  velocity: '',
  duration: '',
  area: '',
  proximalLatency: '',
  proximalAmplitude: '',
  proximalDuration: '',
  proximalArea: '',
  proximalStimulationSite: '',
  segmentDistance: '',
  segmentDistanceUnit: 'cm',
  segmentName: '',
  notes: ''
};

const initialSpecialStudyInputState: CurrentSpecialStudyInput = {
  studyId: '',
  side: 'bilateral',
  values: {},
  notes: ''
};

const normalizeNerveLabel = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/nervio|motor|sensitivo|sensorial|comun/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const NeuroConductionForm: React.FC<NeuroConductionFormProps> = ({ onSave, initialData, onDraftChange }) => {
  const [collectedResults, setCollectedResults] = React.useState<NCSTestResult[]>([]);
  const [currentInput, setCurrentInput] = React.useState<CurrentNCSInput>(initialCurrentInputState);
  const [editingResultId, setEditingResultId] = React.useState<string | null>(null);
  const [validationErrors, setValidationErrors] = React.useState<Record<string, string>>({});
  
  // 🔥 Physiological validation alerts
  const [physiologicalAlerts, setPhysiologicalAlerts] = React.useState<ValidationAlert[]>([]);
  const [currentPhysiologicalAlerts, setCurrentPhysiologicalAlerts] = React.useState<ValidationAlert[]>([]);
  
  // Special Studies states
  const [specialStudiesNotPerformed, setSpecialStudiesNotPerformed] = React.useState(true);
  const [collectedSpecialStudies, setCollectedSpecialStudies] = React.useState<SpecialStudyTest[]>([]);
  const [currentSpecialStudyInput, setCurrentSpecialStudyInput] = React.useState<CurrentSpecialStudyInput>(initialSpecialStudyInputState);
  const [specialStudyValidationErrors, setSpecialStudyValidationErrors] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!onDraftChange) return;
    onDraftChange({
      ncsResults: collectedResults,
      specialStudies: {
        notPerformed: specialStudiesNotPerformed,
        tests: collectedSpecialStudies,
      },
    });
  }, [collectedResults, collectedSpecialStudies, specialStudiesNotPerformed, onDraftChange]);

  // Auto-fill effect para cargar datos iniciales
  React.useEffect(() => {
    if (initialData) {
      console.log('🔄 NeuroConductionForm: Cargando datos iniciales...', initialData);
      console.log('🔍 Estructura de initialData:', {
        hasInitialData: !!initialData,
        hasNcsResults: !!initialData.ncsResults,
        ncsResultsLength: initialData.ncsResults?.length || 0,
        hasSpecialStudies: !!initialData.specialStudies,
        specialStudiesStructure: initialData.specialStudies
      });
      
      // Cargar resultados NCS existentes
      if (initialData.ncsResults && Array.isArray(initialData.ncsResults) && initialData.ncsResults.length > 0) {
        console.log(`📊 Auto-llenando ${initialData.ncsResults.length} resultados NCS`);
        console.log('🔌 Datos NCS a cargar:', initialData.ncsResults);
        
        // Verificar estructura de cada resultado NCS
        initialData.ncsResults.forEach((ncs, index) => {
          console.log(`   NCS ${index + 1}:`, {
            id: ncs.id,
            nerve: ncs.nerve,
            side: ncs.side,
            type: ncs.type,
            latency: ncs.latency,
            amplitude: ncs.amplitude,
            velocity: ncs.velocity,
            status: ncs.status,
            hasAllRequiredFields: !!(ncs.id && ncs.nerve && ncs.side && ncs.latency !== undefined)
          });
        });
        
        setCollectedResults(initialData.ncsResults);
        console.log('✅ collectedResults actualizado con', initialData.ncsResults.length, 'elementos');
        
        // 🔥 CORRECCIÓN: Eliminar setTimeout anti-patrón
        // El estado se actualiza correctamente sin necesidad de setTimeout
        console.log('🔄 Estado de collectedResults actualizado correctamente');
      } else {
        console.warn('⚠️ No hay datos NCS válidos para cargar:', {
          exists: !!initialData.ncsResults,
          isArray: Array.isArray(initialData.ncsResults),
          length: initialData.ncsResults?.length || 0
        });
      }
      
      // Cargar estudios especiales
      if (initialData.specialStudies) {
        if (initialData.specialStudies.notPerformed) {
          console.log('📝 Estudios especiales marcados como NO realizados');
          setSpecialStudiesNotPerformed(true);
          setCollectedSpecialStudies([]);
        } else if (initialData.specialStudies.tests && initialData.specialStudies.tests.length > 0) {
          console.log(`🔬 Auto-llenando ${initialData.specialStudies.tests.length} estudios especiales`);
          setSpecialStudiesNotPerformed(false);
          setCollectedSpecialStudies(initialData.specialStudies.tests);
        }
      }
      
      console.log('✅ NeuroConductionForm: Datos iniciales cargados exitosamente');
    } else {
      console.warn('⚠️ NeuroConductionForm: No se recibieron datos iniciales');
    }
  }, [initialData]);

  const handleInputChange = (field: keyof CurrentNCSInput, value: string | number) => {
    setCurrentInput(prev => {
      if (field === 'nerveId') {
        const nerveInfo = getExtendedNerveData(String(value));
        return {
          ...prev,
          nerveId: String(value),
          studyMode: nerveInfo?.category === 'sensory' ? 'single_site' : prev.studyMode,
        };
      }

      return { ...prev, [field]: value };
    });
    if (validationErrors[field]) {
      setValidationErrors(prev => { const newErrors = { ...prev }; delete newErrors[field]; return newErrors; });
    }
  };

  const parseOptionalNumber = (value: number | string | undefined): number | undefined => {
    if (value === undefined || value === '') return undefined;
    const parsed = parseFloat(String(value));
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const calculateSegmentVelocity = (
    distanceCm: number | undefined,
    distalLatencyMs: number,
    proximalLatencyMs: number | undefined
  ): number | undefined => {
    if (!distanceCm || !proximalLatencyMs || proximalLatencyMs <= distalLatencyMs) return undefined;
    return Number(((distanceCm * 10) / (proximalLatencyMs - distalLatencyMs)).toFixed(1));
  };

  const toCentimeters = (value: number | undefined, unit: DistanceUnit): number | undefined => {
    if (value === undefined) return undefined;
    return unit === 'mm' ? Number((value / 10).toFixed(2)) : value;
  };

  const buildSegmentName = (
    distalSite?: StimulationSite | '',
    proximalSite?: StimulationSite | '',
    fallback?: string
  ): string | undefined => {
    if (fallback?.trim()) return fallback.trim();
    if (!distalSite || !proximalSite) return undefined;
    const distalLabel = stimulationSiteOptions.find(option => option.value === distalSite)?.label || distalSite;
    const proximalLabel = stimulationSiteOptions.find(option => option.value === proximalSite)?.label || proximalSite;
    return `${distalLabel}-${proximalLabel}`;
  };

  const calculateDropPercent = (distal?: number, proximal?: number): number | undefined => {
    if (!distal || proximal === undefined || distal <= 0) return undefined;
    return Number((((distal - proximal) / distal) * 100).toFixed(1));
  };

  const buildTechnicalWarnings = (
    nerveType: 'motor' | 'sensory',
    input: CurrentNCSInput,
    values: {
      latency: number;
      velocity?: number;
      proximalLatency?: number;
      segmentDistance?: number;
      segmentVelocity?: number;
      amplitudeDropPercent?: number;
      areaDropPercent?: number;
      temporalDispersion?: boolean;
      temperature?: number;
      filterLowHz?: number;
      filterHighHz?: number;
      averagingCount?: number;
    }
  ): string[] => {
    const warnings: string[] = [];
    if (input.studyMode === 'motor_segment' && nerveType === 'motor') {
      if (!values.proximalLatency || !values.segmentDistance) {
        warnings.push('Segmento motor incompleto: se requieren latencia proximal y distancia segmentaria para validar VCM.');
      }
      if (values.segmentVelocity && values.velocity && Math.abs(values.segmentVelocity - values.velocity) > 8) {
        warnings.push(`VCM reportada (${values.velocity} m/s) difiere de la calculada (${values.segmentVelocity} m/s).`);
      }
      if (values.amplitudeDropPercent !== undefined && values.amplitudeDropPercent >= 50) {
        warnings.push(`Caída de amplitud proximal/distal ${values.amplitudeDropPercent}%: compatible con bloqueo de conducción si la técnica es adecuada.`);
      } else if (values.amplitudeDropPercent !== undefined && values.amplitudeDropPercent >= 30) {
        warnings.push(`Caída de amplitud proximal/distal ${values.amplitudeDropPercent}%: revisar dispersión temporal, temperatura y técnica.`);
      }
      if (values.areaDropPercent !== undefined && values.areaDropPercent >= 50) {
        warnings.push(`Caída de área ${values.areaDropPercent}%: apoya bloqueo de conducción frente a dispersión temporal aislada.`);
      }
      if (values.temporalDispersion) {
        warnings.push('Duración proximal >30% respecto a distal: sugiere dispersión temporal.');
      }
    }

    if (nerveType === 'sensory') {
      if (!input.sensoryTechnique) warnings.push('Seleccione técnica sensitiva ortodrómica o antidrómica.');
      if (values.averagingCount !== undefined && values.averagingCount < 10) {
        warnings.push('Promediación baja para SNAP: considere aumentar estímulos para estabilizar línea base.');
      }
      if (values.filterLowHz !== undefined && values.filterHighHz !== undefined && values.filterLowHz >= values.filterHighHz) {
        warnings.push('Filtro bajo debe ser menor que filtro alto.');
      }
    }

    if (values.temperature !== undefined && values.temperature < 30) {
      warnings.push('Temperatura cutánea <30 °C: puede prolongar latencias y reducir VCM.');
    }

    return warnings;
  };

  const getSelectedNerveData = (): ExtendedNerveData | undefined => {
    return getExtendedNerveData(currentInput.nerveId);
  };

  const selectedNerveData = getSelectedNerveData();

  const findNerveIdForResult = (result: NCSTestResult): string => {
    const resultLabel = normalizeNerveLabel(result.nerve);
    const exactMatch = nerveDatabase.find(nerve => {
      const nerveInfo = getExtendedNerveData(nerve.id);
      return nerveInfo?.category === result.type && normalizeNerveLabel(nerve.name) === resultLabel;
    });
    if (exactMatch) return exactMatch.id;

    const partialMatch = nerveDatabase.find(nerve => {
      const nerveInfo = getExtendedNerveData(nerve.id);
      if (nerveInfo?.category !== result.type) return false;

      const nerveLabel = normalizeNerveLabel(nerve.name);
      return nerveLabel.includes(resultLabel) || resultLabel.includes(nerveLabel.split(' ')[0]);
    });

    return partialMatch?.id || '';
  };

  const refreshPhysiologicalAlerts = (results: NCSTestResult[]) => {
    const allAlerts: ValidationAlert[] = [];
    results.forEach(r => {
      allAlerts.push(...PhysiologicalValidationService.validateNCSResult(r));
    });
    setPhysiologicalAlerts(allAlerts);
  };

  const applyScreeningPreset = (presetId: ScreeningPresetId) => {
    const preset = screeningPresets.find(p => p.id === presetId);
    if (!preset) return;

    const availableNerveIds = preset.nerveIds.filter(id => getExtendedNerveData(id));
    const firstNerveId = availableNerveIds[0] || '';
    setCurrentInput(prev => ({
      ...prev,
      nerveId: firstNerveId,
      studyMode: 'single_site',
      sensoryTechnique: 'antidromic',
      notes: `Preset ${preset.label}: ${preset.description} Nervios sugeridos: ${availableNerveIds.map(id => getExtendedNerveData(id)?.name || id).join('; ')}.`,
    }));

    if (preset.specialStudyIds?.length) {
      setSpecialStudiesNotPerformed(false);
      const firstStudy = availableSpecialStudies.find(study => preset.specialStudyIds?.includes(study.id));
      if (firstStudy) {
        setCurrentSpecialStudyInput(prev => ({
          ...prev,
          studyId: firstStudy.id,
          side: firstStudy.sideOptions.includes('bilateral') ? 'bilateral' : firstStudy.sideOptions[0],
        }));
      }
    }
  };

  const validateCurrentInput = (): boolean => {
    const errors: Record<string, string> = {};
    if (!currentInput.nerveId) errors.nerveId = 'Debe seleccionar un nervio.';
    
    const lat = parseFloat(String(currentInput.latency));
    const amp = parseFloat(String(currentInput.amplitude));
    const vel = parseOptionalNumber(currentInput.velocity);
    const nerveInfo = getSelectedNerveData();
    const proxLat = parseOptionalNumber(currentInput.proximalLatency);
    const proxAmp = parseOptionalNumber(currentInput.proximalAmplitude);
    const segmentDistance = parseOptionalNumber(currentInput.segmentDistance);
    const isMotorSegment = nerveInfo?.category === 'motor' && currentInput.studyMode === 'motor_segment';

    if (isNaN(lat) || lat <= 0) errors.latency = 'Latencia inválida (>0).';
    if (isNaN(amp) || amp <= 0) errors.amplitude = 'Amplitud inválida (>0).';
    if (!isMotorSegment && (!vel || vel <= 0)) errors.velocity = 'Velocidad inválida (>0).';
    if (isMotorSegment) {
      if (!proxLat || proxLat <= lat) errors.proximalLatency = 'Latencia proximal debe ser mayor que la distal.';
      if (!proxAmp || proxAmp <= 0) errors.proximalAmplitude = 'Amplitud proximal inválida (>0).';
      if (!segmentDistance || segmentDistance <= 0) errors.segmentDistance = `Distancia segmentaria requerida (>0 ${currentInput.segmentDistanceUnit}).`;
      if (vel !== undefined && vel <= 0) errors.velocity = 'VCM reportada inválida (>0) o deje el campo vacío para calcularla.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const addNCSResult = () => {
    if (!validateCurrentInput()) return;

    const nerveInfo = getSelectedNerveData();
    if (!nerveInfo) {
      setValidationErrors(prev => ({...prev, nerveId: "Nervio seleccionado no válido."}))  
      return;
    }

    const lat = parseFloat(String(currentInput.latency));
    const amp = parseFloat(String(currentInput.amplitude));
    const reportedVelocity = parseOptionalNumber(currentInput.velocity);
    const peakLat = parseOptionalNumber(currentInput.peakLatency);
    const dur = parseOptionalNumber(currentInput.duration);
    const areaVal = parseOptionalNumber(currentInput.area);
    const enteredDistance = parseOptionalNumber(currentInput.distance);
    const distance = toCentimeters(enteredDistance, currentInput.distanceUnit);
    const temperature = parseOptionalNumber(currentInput.temperature);
    const filterLowHz = parseOptionalNumber(currentInput.filterLowHz);
    const filterHighHz = parseOptionalNumber(currentInput.filterHighHz);
    const averagingCount = parseOptionalNumber(currentInput.averagingCount);
    const proximalLatency = parseOptionalNumber(currentInput.proximalLatency);
    const proximalAmplitude = parseOptionalNumber(currentInput.proximalAmplitude);
    const proximalDuration = parseOptionalNumber(currentInput.proximalDuration);
    const proximalArea = parseOptionalNumber(currentInput.proximalArea);
    const enteredSegmentDistance = parseOptionalNumber(currentInput.segmentDistance);
    const segmentDistance = toCentimeters(enteredSegmentDistance, currentInput.segmentDistanceUnit);
    const latencyDifference = proximalLatency !== undefined ? Number((proximalLatency - lat).toFixed(1)) : undefined;
    const segmentVelocity = calculateSegmentVelocity(segmentDistance, lat, proximalLatency);
    const vel = segmentVelocity || reportedVelocity || 0;
    const amplitudeDropPercent = calculateDropPercent(amp, proximalAmplitude);
    const areaDropPercent = calculateDropPercent(areaVal, proximalArea);
    const temporalDispersion =
      dur !== undefined && proximalDuration !== undefined && dur > 0
        ? proximalDuration > dur * 1.3
        : undefined;

    const abnormalParameters: NonNullable<NCSTestResult['abnormalParameters']> = [];
    const findings: string[] = [];

    if (nerveInfo.referenceValues.latency && (lat < nerveInfo.referenceValues.latency.min || lat > nerveInfo.referenceValues.latency.max)) {
      abnormalParameters.push('latency');
      findings.push(`Latencia (${lat}ms) fuera de rango (${nerveInfo.referenceValues.latency.min}-${nerveInfo.referenceValues.latency.max}ms).`);
    }
    if (nerveInfo.referenceValues.amplitude && (amp < nerveInfo.referenceValues.amplitude.min || amp > nerveInfo.referenceValues.amplitude.max)) {
      abnormalParameters.push('amplitude');
      findings.push(`Amplitud (${amp}${nerveInfo.defaultAmplitudeUnit}) fuera de rango (${nerveInfo.referenceValues.amplitude.min}-${nerveInfo.referenceValues.amplitude.max}${nerveInfo.defaultAmplitudeUnit}).`);
    }
    if (nerveInfo.referenceValues.velocity && (vel < nerveInfo.referenceValues.velocity.min || vel > nerveInfo.referenceValues.velocity.max)) {
      abnormalParameters.push('velocity');
      findings.push(`Velocidad (${vel}m/s) fuera de rango (${nerveInfo.referenceValues.velocity.min}-${nerveInfo.referenceValues.velocity.max}m/s).`);
    }

    const technicalWarnings = buildTechnicalWarnings(nerveInfo.category, currentInput, {
      latency: lat,
      velocity: reportedVelocity,
      proximalLatency,
      segmentDistance,
      segmentVelocity,
      amplitudeDropPercent,
      areaDropPercent,
      temporalDispersion,
      temperature,
      filterLowHz,
      filterHighHz,
      averagingCount,
    });
    findings.push(...technicalWarnings);

    const status: NCSTestResult['status'] = abnormalParameters.length > 0 ? 'abnormal' : 'normal';
    if (findings.length === 0 && status === 'normal') {
        findings.push("Estudio dentro de límites normales.");
    }

    const qualitativeFindings = currentInput.notes
      ?.split(';')
      .map(note => note.trim())
      .filter(Boolean);

    const existingResult = editingResultId ? collectedResults.find(result => result.id === editingResultId) : undefined;
    const newResult: NCSTestResult = {
      ...existingResult,
      id: editingResultId || crypto.randomUUID(),
      nerve: nerveInfo.name,
      type: nerveInfo.category,
      side: currentInput.side,
      studyMode: currentInput.studyMode,
      sensoryTechnique: nerveInfo.category === 'sensory' ? currentInput.sensoryTechnique : undefined,
      latency: lat,
      peakLatency: nerveInfo.category === 'sensory' ? peakLat : undefined,
      amplitude: amp,
      amplitudeUnit: nerveInfo.defaultAmplitudeUnit,
      velocity: vel,
      duration: dur,
      area: areaVal,
      distance,
      distanceUnit: currentInput.distanceUnit,
      temperature,
      filterLowHz,
      filterHighHz,
      averagingCount,
      stimulationSite: currentInput.stimulationSite || undefined,
      recordingSite: currentInput.recordingSite || undefined,
      segmentName: buildSegmentName(
        currentInput.stimulationSite,
        currentInput.proximalStimulationSite,
        currentInput.segmentName
      ),
      latencyDifference,
      proximalLatency,
      proximalAmplitude,
      proximalDuration,
      proximalArea,
      proximalStimulationSite: currentInput.proximalStimulationSite || undefined,
      segmentDistance,
      segmentDistanceUnit: currentInput.segmentDistanceUnit,
      segmentVelocity,
      reportedSegmentVelocity: reportedVelocity,
      amplitudeDropPercent,
      areaDropPercent,
      temporalDispersion,
      technicalWarnings,
      status,
      abnormalParameters,
      referenceValues: nerveInfo.referenceValues as NCSTestReferenceValues,
      findings,
      qualitativeFindings: qualitativeFindings?.length ? qualitativeFindings : undefined,
    };

    setCollectedResults(prev => {
      const updated = editingResultId
        ? prev.map(result => result.id === editingResultId ? newResult : result)
        : [...prev, newResult];
      refreshPhysiologicalAlerts(updated);
      return updated;
    });
    setEditingResultId(null);
    setCurrentInput(initialCurrentInputState);
    setValidationErrors({});
  };

  const removeNCSResult = (id: string) => {
    setCollectedResults(prev => {
      const updated = prev.filter(r => r.id !== id);
      refreshPhysiologicalAlerts(updated);
      return updated;
    });
    if (editingResultId === id) {
      setEditingResultId(null);
      setCurrentInput(initialCurrentInputState);
      setValidationErrors({});
    }
  };

  const editNCSResult = (result: NCSTestResult) => {
    setEditingResultId(result.id);
    setValidationErrors({});
    setCurrentInput({
      nerveId: findNerveIdForResult(result),
      side: result.side,
      studyMode: result.studyMode || 'single_site',
      sensoryTechnique: result.sensoryTechnique || 'antidromic',
      stimulationSite: result.stimulationSite || '',
      recordingSite: result.recordingSite || '',
      distance: result.distanceUnit === 'mm' && result.distance !== undefined ? result.distance * 10 : result.distance ?? '',
      distanceUnit: result.distanceUnit || 'cm',
      temperature: result.temperature ?? '',
      filterLowHz: result.filterLowHz ?? '',
      filterHighHz: result.filterHighHz ?? '',
      averagingCount: result.averagingCount ?? '',
      latency: result.latency || '',
      peakLatency: result.peakLatency ?? '',
      amplitude: result.amplitude || '',
      velocity: (result.reportedSegmentVelocity ?? result.velocity) || '',
      duration: result.duration ?? '',
      area: result.area ?? '',
      proximalLatency: result.proximalLatency ?? '',
      proximalAmplitude: result.proximalAmplitude ?? '',
      proximalDuration: result.proximalDuration ?? '',
      proximalArea: result.proximalArea ?? '',
      proximalStimulationSite: result.proximalStimulationSite || '',
      segmentDistance: result.segmentDistanceUnit === 'mm' && result.segmentDistance !== undefined ? result.segmentDistance * 10 : result.segmentDistance ?? '',
      segmentDistanceUnit: result.segmentDistanceUnit || 'cm',
      segmentName: result.segmentName || '',
      notes: result.qualitativeFindings?.join('; ') || '',
    });
  };

  const cancelNCSResultEdit = () => {
    setEditingResultId(null);
    setCurrentInput(initialCurrentInputState);
    setValidationErrors({});
  };

  React.useEffect(() => {
    const nerveInfo = getSelectedNerveData();
    const lat = parseFloat(String(currentInput.latency));
    const amp = parseFloat(String(currentInput.amplitude));
    const reportedVelocity = parseOptionalNumber(currentInput.velocity);
    const proximalLatency = parseOptionalNumber(currentInput.proximalLatency);
    const segmentDistance = toCentimeters(parseOptionalNumber(currentInput.segmentDistance), currentInput.segmentDistanceUnit);
    const calculatedVelocity = calculateSegmentVelocity(segmentDistance, lat, proximalLatency);
    const vel = calculatedVelocity || reportedVelocity;
    if (!nerveInfo || !Number.isFinite(lat) || !Number.isFinite(amp) || !Number.isFinite(vel || NaN)) {
      setCurrentPhysiologicalAlerts([]);
      return;
    }

    const tempResult: NCSTestResult = {
      id: 'current',
      nerve: nerveInfo.name,
      type: nerveInfo.category,
      side: currentInput.side,
      latency: lat,
      amplitude: amp,
      amplitudeUnit: nerveInfo.defaultAmplitudeUnit,
      velocity: vel || 0,
      temperature: parseOptionalNumber(currentInput.temperature),
      duration: parseOptionalNumber(currentInput.duration),
      area: parseOptionalNumber(currentInput.area),
      proximalLatency,
      segmentDistance,
      segmentVelocity: calculatedVelocity,
      reportedSegmentVelocity: reportedVelocity,
      status: 'undetermined',
    };

    setCurrentPhysiologicalAlerts(
      PhysiologicalValidationService.validateNCSResult(tempResult).filter(alert => alert.severity === 'error')
    );
  }, [
    currentInput.nerveId,
    currentInput.side,
    currentInput.latency,
    currentInput.amplitude,
    currentInput.velocity,
    currentInput.proximalLatency,
    currentInput.segmentDistance,
    currentInput.segmentDistanceUnit,
    currentInput.temperature,
    currentInput.duration,
    currentInput.area,
  ]);

  // Special Studies functions
  const handleSpecialStudyInputChange = (field: keyof CurrentSpecialStudyInput, value: string | number | Record<string, number | string>) => {
    setCurrentSpecialStudyInput(prev => ({ ...prev, [field]: value }));
    if (specialStudyValidationErrors[field]) {
      setSpecialStudyValidationErrors(prev => { const newErrors = { ...prev }; delete newErrors[field]; return newErrors; });
    }
  };

  const handleSpecialStudyValueChange = (parameter: string, value: string | number) => {
    setCurrentSpecialStudyInput(prev => ({
      ...prev,
      values: { ...prev.values, [parameter]: value }
    }));
  };

  const getSelectedSpecialStudyData = () => {
    return availableSpecialStudies.find(s => s.id === currentSpecialStudyInput.studyId);
  };

  const validateSpecialStudyInput = (): boolean => {
    const errors: Record<string, string> = {};
    if (!currentSpecialStudyInput.studyId) errors.studyId = 'Debe seleccionar un estudio especial.';
    
    const selectedStudy = getSelectedSpecialStudyData();
    if (selectedStudy) {
      for (const parameter of selectedStudy.parameters) {
        const value = parseFloat(String(currentSpecialStudyInput.values[parameter] || ''));
        if (isNaN(value) || value < 0) {
          errors[parameter] = `${parameter} inválido (≥0).`;
        }
      }
    }

    setSpecialStudyValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const addSpecialStudyResult = () => {
    if (!validateSpecialStudyInput()) return;

    const studyInfo = getSelectedSpecialStudyData();
    if (!studyInfo) {
      setSpecialStudyValidationErrors(prev => ({...prev, studyId: "Estudio seleccionado no válido."}));
      return;
    }

    const abnormalParameters: string[] = [];
    const findings: string[] = [];

    // Convert values to numbers and check against reference values
    const processedValues: Record<string, number> = {};
    for (const parameter of studyInfo.parameters) {
      const value = parseFloat(String(currentSpecialStudyInput.values[parameter] || '0'));
      processedValues[parameter] = value;

      const refRange = studyInfo.referenceValues[parameter];
      if (refRange && (value < refRange.min || value > refRange.max)) {
        abnormalParameters.push(parameter);
        const unit = studyInfo.units[parameter] || '';
        findings.push(`${parameter} (${value}${unit}) fuera de rango (${refRange.min}-${refRange.max}${unit}).`);
      }
    }

    const status: SpecialStudyTest['status'] = abnormalParameters.length > 0 ? 'abnormal' : 'normal';
    if (findings.length === 0 && status === 'normal') {
      findings.push("Estudio dentro de límites normales.");
    }

    const crossoverAmp =
      studyInfo.id === 'martin_gruber_crossover' &&
      typeof processedValues.pamcMedianStimElbowAdm === 'number'
        ? processedValues.pamcMedianStimElbowAdm
        : undefined;

    const newSpecialStudy: SpecialStudyTest = {
      id: crypto.randomUUID(),
      name: studyInfo.name,
      type: toSpecialStudyTestType(studyInfo.type),
      side: currentSpecialStudyInput.side,
      values: processedValues,
      amplitude: crossoverAmp,
      status,
      abnormalParameters,
      referenceValues: studyInfo.referenceValues,
      findings
    };

    setCollectedSpecialStudies(prev => [...prev, newSpecialStudy]);
    setCurrentSpecialStudyInput(initialSpecialStudyInputState);
    setSpecialStudyValidationErrors({});
    
    // Auto-deselect "not performed" when adding a special study
    if (specialStudiesNotPerformed) {
      setSpecialStudiesNotPerformed(false);
    }
  };

  const removeSpecialStudyResult = (id: string) => {
    setCollectedSpecialStudies(prev => prev.filter(s => s.id !== id));
    
    // If no special studies left, can optionally re-enable "not performed"
    if (collectedSpecialStudies.length === 1) { // Will be 0 after removal
      setSpecialStudiesNotPerformed(true);
    }
  };

  const handleSpecialStudiesNotPerformedChange = (notPerformed: boolean) => {
    setSpecialStudiesNotPerformed(notPerformed);
    if (notPerformed) {
      // Clear all special studies if "not performed" is selected
      setCollectedSpecialStudies([]);
      setCurrentSpecialStudyInput(initialSpecialStudyInputState);
      setSpecialStudyValidationErrors({});
    }
  };

  const handleCompleteStudy = () => {
    if (collectedResults.length === 0) {
      setValidationErrors({ form: 'Debe agregar al menos una medición de neuroconducción.' });
      return;
    }
    
    const specialStudiesData: SpecialStudyData = {
      notPerformed: specialStudiesNotPerformed,
      tests: collectedSpecialStudies
    };

    onSave({ 
      ncsResults: collectedResults, 
      specialStudies: specialStudiesData 
    });
  };
  
  const isValueOutOfRangeLocal = (valueStr: string | number, parameter: 'latency' | 'amplitude' | 'velocity' | 'duration' | 'area'): boolean => {
    const value = parseFloat(String(valueStr));
    if (isNaN(value) || value <= 0) return false;

    const nerveInfo = getSelectedNerveData();
    if (!nerveInfo) return false;

    const refRange = nerveInfo.referenceValues[parameter as keyof typeof nerveInfo.referenceValues] as ReferenceValueRange | undefined;
    if (!refRange) return false;
    
    return value < refRange.min || value > refRange.max;
  };

  const isSpecialStudyValueOutOfRange = (parameter: string, valueStr: string | number): boolean => {
    const value = parseFloat(String(valueStr));
    if (isNaN(value) || value < 0) return false;

    const studyInfo = getSelectedSpecialStudyData();
    if (!studyInfo) return false;

    const refRange = studyInfo.referenceValues[parameter];
    if (!refRange) return false;
    
    return value < refRange.min || value > refRange.max;
  };

  const getNerveTypeLabel = (type: 'motor' | 'sensory'): string => type === 'motor' ? 'Motor' : 'Sensitivo';

  const getNerveTypeHint = (type: 'motor' | 'sensory'): string =>
    type === 'motor'
      ? 'Estudio motor: amplitud en mV y permite segmento distal/proximal.'
      : 'Estudio sensitivo: amplitud en µV, muestra técnica sensitiva y latencia pico.';

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {validationErrors.form && (
        <div className="p-3 bg-red-900/30 border border-red-700/50 rounded-md flex items-start mb-4">
          <AlertTriangle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
          <span className="text-red-300 text-sm">{validationErrors.form}</span>
        </div>
      )}

      {/* Improved instructions */}
      <div className="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-blue-300 mb-2 flex items-center">
          <Activity className="h-5 w-5 mr-2" />
          Estudios de Neuroconducción
        </h3>
        <p className="text-blue-200 text-sm">
          Complete las mediciones de velocidades de conducción nerviosa, amplitudes y latencias para cada nervio estudiado. 
          Puede agregar estudios especiales como respuestas tardías en la sección correspondiente.
        </p>
      </div>

      <div className="bg-gray-700/30 p-4 rounded-lg border border-gray-600">
        <h3 className="text-md font-semibold text-gray-100 mb-3">Screening Rápido</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {screeningPresets.map(preset => (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyScreeningPreset(preset.id)}
              className="text-left p-3 rounded-md bg-gray-800/70 border border-gray-600 hover:border-blue-500 transition-colors"
            >
              <span className="block text-sm font-semibold text-blue-200">{preset.label}</span>
              <span className="block text-xs text-gray-400 mt-1">{preset.description}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Los presets sólo preparan estudios sugeridos; no reemplazan el criterio clínico ni agregan mediciones automáticas.
        </p>
      </div>
      
      <div className="bg-gray-700/30 p-4 sm:p-6 rounded-lg border border-gray-600">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold text-gray-100">
            {editingResultId ? 'Editar Medición de Neuroconducción' : 'Agregar Medición de Neuroconducción'}
          </h3>
          {editingResultId && (
            <button
              type="button"
              onClick={cancelNCSResultEdit}
              className="inline-flex items-center self-start sm:self-auto px-3 py-1.5 text-sm rounded-md border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors"
            >
              <X className="mr-2 h-4 w-4" /> Cancelar edición
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_220px_minmax(0,1fr)] gap-x-6 gap-y-4 mb-4">
          <div>
            <label htmlFor="nerveId" className="block text-sm font-medium text-gray-300 mb-1">Nervio</label>
            <select
              id="nerveId"
              name="nerveId"
              value={currentInput.nerveId}
              onChange={(e) => handleInputChange('nerveId', e.target.value)}
              className={`w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm ${validationErrors.nerveId ? 'border-red-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'}`}
            >
              <option value="">Seleccionar nervio...</option>
              <optgroup label="MMSS - Motores">
                {getMotorNerves('upper').map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
              </optgroup>
              <optgroup label="MMSS - Sensitivos">
                {getSensoryNerves('upper').map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
              </optgroup>
              <optgroup label="MMII - Motores">
                {getMotorNerves('lower').map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
              </optgroup>
              <optgroup label="MMII - Sensitivos">
                {getSensoryNerves('lower').map(n => <option key={n.id} value={n.id}>{n.name}</option>)} 
              </optgroup>
            </select>
            {validationErrors.nerveId && <p className="mt-1 text-xs text-red-400">{validationErrors.nerveId}</p>}
            
            {/* ALERTAS FISIOLÓGICAS EN TIEMPO REAL */}
            {currentPhysiologicalAlerts.length > 0 && (
              <div className="mt-2 text-xs p-2 bg-red-900/40 border border-red-500 rounded text-red-300">
                <span className="font-bold">⚠️ Imposibilidad Fisiológica:</span>
                <ul className="list-disc pl-4 mt-1">
                  {currentPhysiologicalAlerts.map((alert, idx) => (
                    <li key={idx}>{alert.message}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {currentInput.nerveId && getSelectedNerveData() && (
              <div className="mt-2 text-xs text-gray-400 space-y-0.5">
                <p>Ref: Lat ({getSelectedNerveData()!.referenceValues.latency.min}-{getSelectedNerveData()!.referenceValues.latency.max}ms) 
                   Amp ({getSelectedNerveData()!.referenceValues.amplitude.min}-{getSelectedNerveData()!.referenceValues.amplitude.max}{getSelectedNerveData()!.defaultAmplitudeUnit}) 
                   Vel ({getSelectedNerveData()!.referenceValues.velocity.min}-{getSelectedNerveData()!.referenceValues.velocity.max}m/s)
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Tipo de Estudio</label>
            {selectedNerveData ? (
              <div className={`rounded-md border p-3 ${
                selectedNerveData.category === 'motor'
                  ? 'bg-blue-900/30 border-blue-600/60'
                  : 'bg-emerald-900/30 border-emerald-600/60'
              }`}>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                  selectedNerveData.category === 'motor'
                    ? 'bg-blue-600 text-white'
                    : 'bg-emerald-600 text-white'
                }`}>
                  {getNerveTypeLabel(selectedNerveData.category)}
                </span>
                <p className="mt-2 text-xs text-gray-300">{getNerveTypeHint(selectedNerveData.category)}</p>
              </div>
            ) : (
              <div className="rounded-md border border-gray-700 bg-gray-800/40 p-3 text-sm text-gray-400">
                Selecciona un nervio para definir si es motor o sensitivo.
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Lado</label>
            <div className="flex space-x-3">
              {(['left', 'right'] as const).map(sideOption => (
                <button
                  key={sideOption}
                  type="button"
                  onClick={() => handleInputChange('side', sideOption)}
                  className={`px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${currentInput.side === sideOption ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                >
                  {sideOption === 'left' ? 'Izquierdo' : 'Derecho'}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-4 p-4 bg-gray-800/40 rounded-lg border border-gray-700">
          <h4 className="text-sm font-semibold text-gray-200 mb-3">Técnica y Seguridad del Registro</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Modo de Estudio</label>
              <select
                value={currentInput.studyMode}
                onChange={(e) => handleInputChange('studyMode', e.target.value)}
                className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="single_site">Medición distal/simple</option>
                <option value="motor_segment" disabled={selectedNerveData?.category === 'sensory'}>Segmento motor distal/proximal</option>
              </select>
            </div>
            {selectedNerveData?.category === 'sensory' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Técnica Sensitiva</label>
                <select
                  value={currentInput.sensoryTechnique}
                  onChange={(e) => handleInputChange('sensoryTechnique', e.target.value)}
                  className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="antidromic">Antidrómica</option>
                  <option value="orthodromic">Ortodrómica</option>
                </select>
              </div>
            )}
            <div>
              <label htmlFor="temperature" className="block text-sm font-medium text-gray-300 mb-1">Temperatura (°C)</label>
              <input id="temperature" type="number" step="0.1" value={currentInput.temperature} onChange={(e) => handleInputChange('temperature', e.target.value)}
                className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500" />
            </div>
            <div>
              <label htmlFor="distance" className="block text-sm font-medium text-gray-300 mb-1">Distancia</label>
              <div className="flex gap-2">
                <input id="distance" type="number" step="0.1" value={currentInput.distance} onChange={(e) => handleInputChange('distance', e.target.value)}
                  className="min-w-0 flex-1 p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500" />
                <select value={currentInput.distanceUnit} onChange={(e) => handleInputChange('distanceUnit', e.target.value)}
                  className="w-20 p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500">
                  <option value="cm">cm</option>
                  <option value="mm">mm</option>
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="stimulationSite" className="block text-sm font-medium text-gray-300 mb-1">Sitio Estimulación</label>
              <select id="stimulationSite" value={currentInput.stimulationSite} onChange={(e) => handleInputChange('stimulationSite', e.target.value)}
                className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500">
                <option value="">No especificado</option>
                {stimulationSiteOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="recordingSite" className="block text-sm font-medium text-gray-300 mb-1">Sitio Registro</label>
              <input id="recordingSite" type="text" value={currentInput.recordingSite} onChange={(e) => handleInputChange('recordingSite', e.target.value)}
                placeholder="APB, ADM, EDB, dedo II..."
                className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 placeholder-gray-500 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500" />
            </div>
            <div>
              <label htmlFor="filterLowHz" className="block text-sm font-medium text-gray-300 mb-1">Filtro Bajo (Hz)</label>
              <input id="filterLowHz" type="number" step="1" value={currentInput.filterLowHz} onChange={(e) => handleInputChange('filterLowHz', e.target.value)}
                className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500" />
            </div>
            <div>
              <label htmlFor="filterHighHz" className="block text-sm font-medium text-gray-300 mb-1">Filtro Alto (Hz)</label>
              <input id="filterHighHz" type="number" step="1" value={currentInput.filterHighHz} onChange={(e) => handleInputChange('filterHighHz', e.target.value)}
                className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500" />
            </div>
            {selectedNerveData?.category === 'sensory' && (
              <div>
                <label htmlFor="averagingCount" className="block text-sm font-medium text-gray-300 mb-1">Promediación (#)</label>
                <input id="averagingCount" type="number" step="1" value={currentInput.averagingCount} onChange={(e) => handleInputChange('averagingCount', e.target.value)}
                  className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500" />
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Registrar técnica, filtros, temperatura y distancia evita interpretar como patología artefactos por configuración o extremidad fría.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-6 gap-y-4 mb-4">
          <div>
            <label htmlFor="latency" className="block text-sm font-medium text-gray-300 mb-1">Latencia (ms)</label>
            <input id="latency" type="number" step="0.1" value={currentInput.latency} onChange={(e) => handleInputChange('latency', e.target.value)}
              className={`w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 placeholder-gray-500 text-sm ${validationErrors.latency ? 'border-red-500' : isValueOutOfRangeLocal(currentInput.latency, 'latency') ? 'border-yellow-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'}`}
            />
            {validationErrors.latency && <p className="mt-1 text-xs text-red-400">{validationErrors.latency}</p>}
          </div>
          {selectedNerveData?.category === 'sensory' && (
            <div>
              <label htmlFor="peakLatency" className="block text-sm font-medium text-gray-300 mb-1">Lat. Pico (ms)</label>
              <input id="peakLatency" type="number" step="0.1" value={currentInput.peakLatency || ''} onChange={(e) => handleInputChange('peakLatency', e.target.value)}
                className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 placeholder-gray-500 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500" />
            </div>
          )}
          <div>
            <label htmlFor="amplitude" className="block text-sm font-medium text-gray-300 mb-1">Amplitud ({getSelectedNerveData()?.defaultAmplitudeUnit || 'µV/mV'})</label>
            <input id="amplitude" type="number" step="0.1" value={currentInput.amplitude} onChange={(e) => handleInputChange('amplitude', e.target.value)}
              className={`w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 placeholder-gray-500 text-sm ${validationErrors.amplitude ? 'border-red-500' : isValueOutOfRangeLocal(currentInput.amplitude, 'amplitude') ? 'border-yellow-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'}`}
            />
            {validationErrors.amplitude && <p className="mt-1 text-xs text-red-400">{validationErrors.amplitude}</p>}
          </div>
          <div>
            <label htmlFor="velocity" className="block text-sm font-medium text-gray-300 mb-1">
              {selectedNerveData?.category === 'motor' && currentInput.studyMode === 'motor_segment'
                ? 'VCM Reportada (m/s, opc.)'
                : 'Velocidad (m/s)'}
            </label>
            <input id="velocity" type="number" step="0.1" value={currentInput.velocity} onChange={(e) => handleInputChange('velocity', e.target.value)}
              className={`w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 placeholder-gray-500 text-sm ${validationErrors.velocity ? 'border-red-500' : isValueOutOfRangeLocal(currentInput.velocity, 'velocity') ? 'border-yellow-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'}`}
            />
            {validationErrors.velocity && <p className="mt-1 text-xs text-red-400">{validationErrors.velocity}</p>}
          </div>
          <div>
            <label htmlFor="duration" className="block text-sm font-medium text-gray-300 mb-1">Duración (ms) <span className="text-gray-500 text-xs">Opc.</span></label>
            <input id="duration" type="number" step="0.1" value={currentInput.duration || ''} onChange={(e) => handleInputChange('duration', e.target.value)}
              className={`w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 placeholder-gray-500 text-sm ${isValueOutOfRangeLocal(currentInput.duration || '', 'duration') ? 'border-yellow-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'}`}
            />
          </div>
          <div>
            <label htmlFor="area" className="block text-sm font-medium text-gray-300 mb-1">Área (mVms) <span className="text-gray-500 text-xs">Opc.</span></label>
            <input id="area" type="number" step="0.1" value={currentInput.area || ''} onChange={(e) => handleInputChange('area', e.target.value)}
              className={`w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 placeholder-gray-500 text-sm ${isValueOutOfRangeLocal(currentInput.area || '', 'area') ? 'border-yellow-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'}`}
            />
          </div>
        </div>

        {selectedNerveData?.category === 'motor' && currentInput.studyMode === 'motor_segment' && (
          <div className="mb-4 p-4 bg-gray-800/40 rounded-lg border border-gray-700">
            <h4 className="text-sm font-semibold text-gray-200 mb-3">Punto Proximal y Cálculo Segmentario</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-6 gap-y-4">
              <div>
                <label htmlFor="segmentName" className="block text-sm font-medium text-gray-300 mb-1">Segmento</label>
                <input id="segmentName" type="text" value={currentInput.segmentName || ''} onChange={(e) => handleInputChange('segmentName', e.target.value)}
                  placeholder="Tobillo-Fosa poplítea"
                  className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 placeholder-gray-500 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500" />
              </div>
              <div>
                <label htmlFor="proximalStimulationSite" className="block text-sm font-medium text-gray-300 mb-1">Sitio Proximal</label>
                <select id="proximalStimulationSite" value={currentInput.proximalStimulationSite || ''} onChange={(e) => handleInputChange('proximalStimulationSite', e.target.value)}
                  className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500">
                  <option value="">No especificado</option>
                  {stimulationSiteOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="proximalLatency" className="block text-sm font-medium text-gray-300 mb-1">Lat. Prox (ms)</label>
                <input id="proximalLatency" type="number" step="0.1" value={currentInput.proximalLatency || ''} onChange={(e) => handleInputChange('proximalLatency', e.target.value)}
                  className={`w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm ${validationErrors.proximalLatency ? 'border-red-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'}`} />
                {validationErrors.proximalLatency && <p className="mt-1 text-xs text-red-400">{validationErrors.proximalLatency}</p>}
              </div>
              <div>
                <label htmlFor="proximalAmplitude" className="block text-sm font-medium text-gray-300 mb-1">Amp Prox ({selectedNerveData.defaultAmplitudeUnit})</label>
                <input id="proximalAmplitude" type="number" step="0.1" value={currentInput.proximalAmplitude || ''} onChange={(e) => handleInputChange('proximalAmplitude', e.target.value)}
                  className={`w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm ${validationErrors.proximalAmplitude ? 'border-red-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'}`} />
                {validationErrors.proximalAmplitude && <p className="mt-1 text-xs text-red-400">{validationErrors.proximalAmplitude}</p>}
              </div>
              <div>
                <label htmlFor="segmentDistance" className="block text-sm font-medium text-gray-300 mb-1">Dist. Segmento</label>
                <div className="flex gap-2">
                  <input id="segmentDistance" type="number" step="0.1" value={currentInput.segmentDistance || ''} onChange={(e) => handleInputChange('segmentDistance', e.target.value)}
                    className={`min-w-0 flex-1 p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm ${validationErrors.segmentDistance ? 'border-red-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'}`} />
                  <select value={currentInput.segmentDistanceUnit} onChange={(e) => handleInputChange('segmentDistanceUnit', e.target.value)}
                    className="w-20 p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500">
                    <option value="cm">cm</option>
                    <option value="mm">mm</option>
                  </select>
                </div>
                {validationErrors.segmentDistance && <p className="mt-1 text-xs text-red-400">{validationErrors.segmentDistance}</p>}
              </div>
              <div>
                <label htmlFor="proximalDuration" className="block text-sm font-medium text-gray-300 mb-1">Dur Prox (ms)</label>
                <input id="proximalDuration" type="number" step="0.1" value={currentInput.proximalDuration || ''} onChange={(e) => handleInputChange('proximalDuration', e.target.value)}
                  className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500" />
              </div>
              <div>
                <label htmlFor="proximalArea" className="block text-sm font-medium text-gray-300 mb-1">Área Prox</label>
                <input id="proximalArea" type="number" step="0.1" value={currentInput.proximalArea || ''} onChange={(e) => handleInputChange('proximalArea', e.target.value)}
                  className="w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm border-gray-600 focus:border-blue-500 focus:ring-blue-500" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              La VCM segmentaria se calcula con distancia / diferencia de latencias. Puede dejar VCM reportada vacía si la tabla solo trae latencias y distancia.
            </p>
          </div>
        )}
        
        <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-300 mb-1">Notas Adicionales <span className="text-gray-500 text-xs">(Opc.)</span></label>
            <input id="notes" type="text" value={currentInput.notes || ''} onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Interpretación específica, hallazgos cualitativos..."
              className="w-full p-2.5 border border-gray-600 rounded-md bg-gray-800 text-gray-100 placeholder-gray-500 text-sm focus:border-blue-500 focus:ring-blue-500" />
        </div>
        
        <div className="flex justify-end mt-5">
          <button type="button" onClick={addNCSResult}
            className="flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-blue-500 transition-colors text-sm font-medium">
            {editingResultId ? <Save className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
            {editingResultId ? 'Actualizar Medición' : 'Agregar Medición'}
          </button>
        </div>
      </div>
      
      {collectedResults.length > 0 && (
        <>
        <div className="mt-6 bg-gray-700/30 p-4 sm:p-6 rounded-lg border border-gray-600">
          <h3 className="text-lg font-semibold text-gray-100 mb-4">Mediciones de Neuroconducción Registradas</h3>
          <div className="space-y-4">
            {collectedResults.map((result) => (
              <div key={result.id} className={`bg-gray-800/60 p-3 rounded-lg border shadow-md ${result.requiresReview ? 'border-yellow-500/60' : 'border-gray-700'}`}>
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-blue-300">
                    {result.nerve} ({result.side === 'left' ? 'Izq.' : 'Der.'}) <span className={`ml-2 text-[11px] px-2 py-0.5 rounded-full border ${
                      result.type === 'motor'
                        ? 'bg-blue-900/50 text-blue-200 border-blue-700/60'
                        : 'bg-emerald-900/50 text-emerald-200 border-emerald-700/60'
                    }`}>{getNerveTypeLabel(result.type)}</span> - <span className={result.status === 'abnormal' ? 'text-red-400' : result.status === 'undetermined' ? 'text-yellow-400' : 'text-green-400'}>{result.status.toUpperCase()}</span>
                  </h4>
                  <div className="flex items-center gap-2">
                    {result.extractionSource && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-900/50 text-blue-200 border border-blue-700/60">
                        Auto-fill {Math.round((result.extractionSource.confidence || 0) * 100)}%
                      </span>
                    )}
                    {result.requiresReview && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-yellow-900/50 text-yellow-200 border border-yellow-700/60">
                        Revisar
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => editNCSResult(result)}
                      className={`p-1 transition-colors ${editingResultId === result.id ? 'text-blue-300' : 'text-gray-300 hover:text-blue-300'}`}
                      aria-label="Editar medición"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => removeNCSResult(result.id)} className="p-1 text-red-500 hover:text-red-400 transition-colors" aria-label="Eliminar medición">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-1 text-xs">
                  <p>Lat: <span className={result.abnormalParameters?.includes('latency')?'text-red-400 font-bold':'text-gray-300'}>{result.latency > 0 ? `${result.latency}ms` : 'No reportada'}</span></p>
                  {result.peakLatency !== undefined && <p>Lat Pico: <span className="text-gray-300">{result.peakLatency}ms</span></p>}
                  <p>Amp: <span className={result.abnormalParameters?.includes('amplitude') || result.absentResponse ? 'text-red-400 font-bold':'text-gray-300'}>{result.absentResponse ? 'Sin respuesta' : result.amplitude > 0 ? `${result.amplitude}${result.amplitudeUnit || ''}` : 'No reportada'}</span></p>
                  <p>Vel: <span className={result.abnormalParameters?.includes('velocity')?'text-red-400 font-bold':'text-gray-300'}>{result.velocity > 0 ? `${result.velocity}m/s` : 'No reportada'}</span></p>
                  {result.duration !== undefined && <p>Dur: <span className={result.abnormalParameters?.includes('duration')?'text-red-400 font-bold':'text-gray-300'}>{result.duration}ms</span></p>}
                  {result.area !== undefined && <p>Área: <span className={result.abnormalParameters?.includes('area')?'text-red-400 font-bold':'text-gray-300'}>{result.area}mVms</span></p>}
                  {result.temperature !== undefined && <p>Temp: <span className="text-gray-300">{result.temperature}°C</span></p>}
                  {result.distance !== undefined && <p>Dist: <span className="text-gray-300">{formatReportedDistance(result.distance, result.distanceUnit)}</span></p>}
                  {result.stimulationSite && <p>Estim: <span className="text-gray-300">{result.stimulationSite}</span></p>}
                  {result.proximalStimulationSite && <p>Estim Prox: <span className="text-gray-300">{result.proximalStimulationSite}</span></p>}
                  {result.segmentName && <p>Segmento: <span className="text-gray-300">{result.segmentName}</span></p>}
                  {result.latencyDifference !== undefined && <p>Dif Lat: <span className="text-gray-300">{result.latencyDifference}ms</span></p>}
                  {result.proximalLatency !== undefined && <p>Lat Prox: <span className="text-gray-300">{result.proximalLatency}ms</span></p>}
                  {result.proximalAmplitude !== undefined && <p>Amp Prox: <span className="text-gray-300">{result.proximalAmplitude}{result.amplitudeUnit || ''}</span></p>}
                  {result.segmentDistance !== undefined && <p>Dist Seg: <span className="text-gray-300">{formatReportedDistance(result.segmentDistance, result.segmentDistanceUnit)}</span></p>}
                  {result.sensoryTechnique && <p>Técnica: <span className="text-gray-300">{result.sensoryTechnique === 'orthodromic' ? 'Ortodrómica' : 'Antidrómica'}</span></p>}
                  {result.segmentVelocity !== undefined && <p>VCM calc: <span className="text-gray-300">{result.segmentVelocity}m/s</span></p>}
                  {result.reportedSegmentVelocity !== undefined && <p>VCM rep: <span className="text-gray-300">{result.reportedSegmentVelocity}m/s</span></p>}
                  {result.amplitudeDropPercent !== undefined && <p>Drop Amp: <span className={result.amplitudeDropPercent >= 30 ? 'text-yellow-300 font-semibold' : 'text-gray-300'}>{result.amplitudeDropPercent}%</span></p>}
                </div>
                {result.qualitativeFindings && result.qualitativeFindings.length > 0 &&
                    <div className="mt-1.5">
                        <p className="text-xs text-yellow-300">Cualitativo: <span className="text-yellow-100">{result.qualitativeFindings.join('; ')}</span></p>
                    </div>
                }
                {result.extractionSource?.evidence?.[0]?.text &&
                    <div className="mt-1.5">
                        <p className="text-xs text-gray-400">Evidencia: <span className="text-gray-300">{result.extractionSource.evidence[0].text}</span></p>
                    </div>
                }
                {result.findings && result.findings.length > 0 && 
                    <div className="mt-1.5">
                        <p className="text-xs text-gray-400">Hallazgos: <span className="text-gray-300">{result.findings.join('; ')}</span></p>
                    </div>
                }
                {result.referenceValues && 
                    <div className="mt-1 text-xs text-gray-500">
                        (Ref: Lat {result.referenceValues.latency?.min}-{result.referenceValues.latency?.max}ms; 
                        Amp {result.referenceValues.amplitude?.min}-{result.referenceValues.amplitude?.max}{result.amplitudeUnit}; 
                        Vel {result.referenceValues.velocity?.min}-{result.referenceValues.velocity?.max}m/s)
                    </div>
                }
              </div>
            ))}
          </div>
        </div>

        {/* 🔥 Physiological Validation Alerts */}
        {physiologicalAlerts.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-semibold text-yellow-300 flex items-center">
              <ShieldAlert className="h-4 w-4 mr-2" />
              Alertas de Validación Fisiológica
            </h4>
            {physiologicalAlerts.map((alert, index) => (
              <div 
                key={index} 
                className={`p-3 rounded-md border text-sm flex items-start ${
                  alert.severity === 'error' 
                    ? 'bg-red-900/30 border-red-700/50' 
                    : alert.severity === 'warning'
                    ? 'bg-yellow-900/30 border-yellow-700/50'
                    : 'bg-blue-900/30 border-blue-700/50'
                }`}
              >
                {alert.severity === 'error' ? (
                  <AlertTriangle className="h-4 w-4 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
                ) : alert.severity === 'warning' ? (
                  <ShieldAlert className="h-4 w-4 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                ) : (
                  <Info className="h-4 w-4 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <p className={`font-medium ${
                    alert.severity === 'error' ? 'text-red-300' : 
                    alert.severity === 'warning' ? 'text-yellow-300' : 'text-blue-300'
                  }`}>
                    {alert.type === 'impossible' ? '🚫 VALOR IMPOSIBLE' : 
                     alert.type === 'suspicious' ? '⚠️ VALOR SOSPECHOSO' : '📋 NOTA'}
                    {' — '}{alert.parameter}
                  </p>
                  <p className="text-gray-300 mt-0.5">{alert.message}</p>
                  {alert.expectedRange && (
                    <p className="text-gray-400 text-xs mt-0.5">Rango esperado: {alert.expectedRange}</p>
                  )}
                  {alert.recommendation && (
                    <p className="text-gray-400 text-xs mt-1 italic">💡 {alert.recommendation}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        </>
      )}

      {/* Special Studies Section */}
      <div className="bg-gray-700/30 p-4 sm:p-6 rounded-lg border border-gray-600">
        <h3 className="text-lg font-semibold text-gray-100 mb-4 flex items-center">
          <Zap className="h-5 w-5 mr-2 text-yellow-400" />
          Estudios Especiales (Respuestas Tardías)
        </h3>

        {/* Not Performed Toggle */}
        <div className="mb-6">
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => handleSpecialStudiesNotPerformedChange(!specialStudiesNotPerformed)}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                specialStudiesNotPerformed 
                  ? 'bg-gray-600 text-white' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              No se realizaron estudios especiales
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Seleccione esta opción si no se realizaron estudios especiales. Se deseleccionará automáticamente al agregar un estudio.
          </p>
        </div>

        {/* Special Studies Form */}
        {!specialStudiesNotPerformed && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-4">
              <div>
                <label htmlFor="studyId" className="block text-sm font-medium text-gray-300 mb-1">Tipo de Estudio</label>
                <select
                  id="studyId"
                  name="studyId"
                  value={currentSpecialStudyInput.studyId}
                  onChange={(e) => handleSpecialStudyInputChange('studyId', e.target.value)}
                  className={`w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 text-sm ${
                    specialStudyValidationErrors.studyId ? 'border-red-500' : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                >
                  <option value="">Seleccionar estudio especial...</option>
                  {Object.entries(specialStudiesCategories).map(([categoryId, category]) => {
                    const studies = availableSpecialStudies.filter(s => s.category === categoryId);
                    if (studies.length === 0) return null;
                    return (
                      <optgroup key={categoryId} label={category.name}>
                        {studies.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
                {specialStudyValidationErrors.studyId && (
                  <p className="mt-1 text-xs text-red-400">{specialStudyValidationErrors.studyId}</p>
                )}
                
                {currentSpecialStudyInput.studyId && getSelectedSpecialStudyData() && (
                  <div className="mt-2 text-xs text-gray-400">
                    <p>Parámetros: {getSelectedSpecialStudyData()!.parameters.join(', ')}</p>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Lado</label>
                <div className="flex space-x-2">
                  {(['left', 'right', 'bilateral'] as const).map(sideOption => (
                    <button
                      key={sideOption}
                      type="button"
                      onClick={() => handleSpecialStudyInputChange('side', sideOption)}
                      className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                        currentSpecialStudyInput.side === sideOption 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {sideOption === 'left' ? 'Izq.' : sideOption === 'right' ? 'Der.' : 'Bilateral'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Dynamic parameter inputs based on selected study */}
            {currentSpecialStudyInput.studyId && getSelectedSpecialStudyData() && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 mb-4">
                {getSelectedSpecialStudyData()!.parameters.map(parameter => {
                  const studyData = getSelectedSpecialStudyData()!;
                  const unit = studyData.units[parameter] || '';
                  const refRange = studyData.referenceValues[parameter];
                  
                  return (
                    <div key={parameter}>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        {parameter} ({unit})
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={currentSpecialStudyInput.values[parameter] || ''}
                        onChange={(e) => handleSpecialStudyValueChange(parameter, e.target.value)}
                        className={`w-full p-2.5 border rounded-md bg-gray-800 text-gray-100 placeholder-gray-500 text-sm ${
                          specialStudyValidationErrors[parameter] 
                            ? 'border-red-500' 
                            : isSpecialStudyValueOutOfRange(parameter, currentSpecialStudyInput.values[parameter] || 0)
                              ? 'border-yellow-500' 
                              : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'
                        }`}
                      />
                      {refRange && (
                        <p className="text-xs text-gray-500 mt-1">
                          Ref: {refRange.min}-{refRange.max}{unit}
                        </p>
                      )}
                      {specialStudyValidationErrors[parameter] && (
                        <p className="mt-1 text-xs text-red-400">{specialStudyValidationErrors[parameter]}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="specialStudyNotes" className="block text-sm font-medium text-gray-300 mb-1">
                Notas Adicionales <span className="text-gray-500 text-xs">(Opc.)</span>
              </label>
              <input
                id="specialStudyNotes"
                type="text"
                value={currentSpecialStudyInput.notes || ''}
                onChange={(e) => handleSpecialStudyInputChange('notes', e.target.value)}
                placeholder="Hallazgos adicionales, interpretación específica..."
                className="w-full p-2.5 border border-gray-600 rounded-md bg-gray-800 text-gray-100 placeholder-gray-500 text-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={addSpecialStudyResult}
                disabled={!currentSpecialStudyInput.studyId}
                className="flex items-center px-5 py-2.5 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                <Plus className="mr-2 h-4 w-4" /> Agregar Estudio Especial
              </button>
            </div>
          </>
        )}

        {/* Display collected special studies */}
        {collectedSpecialStudies.length > 0 && (
          <div className="mt-6 border-t border-gray-600 pt-6">
            <h4 className="text-md font-semibold text-gray-200 mb-3">Estudios Especiales Registrados</h4>
            <div className="space-y-3">
              {collectedSpecialStudies.map((study) => (
                <div key={study.id} className="bg-gray-800/60 p-3 rounded-lg border border-gray-700">
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-semibold text-yellow-300">
                      {study.name} ({study.side}) - <span className={
                        study.status === 'abnormal'
                          ? 'text-red-400'
                          : study.status === 'undetermined'
                            ? 'text-yellow-300'
                            : 'text-green-400'
                      }>
                        {study.status.toUpperCase()}
                      </span>
                    </h5>
                    <button 
                      onClick={() => removeSpecialStudyResult(study.id)} 
                      className="p-1 text-red-500 hover:text-red-400 transition-colors" 
                      aria-label="Eliminar estudio especial"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                    {Object.entries(study.values).map(([param, value]) => (
                      <p key={param}>
                        {param}: <span className={study.abnormalParameters?.includes(param) ? 'text-red-400 font-bold' : 'text-gray-300'}>
                          {String(value)}
                        </span>
                      </p>
                    ))}
                  </div>
                  {study.findings && study.findings.length > 0 && (
                    <div className="mt-1.5">
                      <p className="text-xs text-gray-400">
                        Hallazgos: <span className="text-gray-300">{study.findings.join('; ')}</span>
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="mt-8 bg-blue-900/20 border border-blue-700/50 p-4 sm:p-6 rounded-lg text-sm">
        <h3 className="text-md font-semibold text-blue-300 mb-3 flex items-center">
          <Activity className="h-5 w-5 mr-2" /> Interpretación General de Patrones NCS
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <h4 className="font-medium text-blue-200 mb-1">Desmielinizante</h4>
            <ul className="list-disc pl-5 text-blue-300/90 text-xs space-y-0.5">
              <li>Velocidad de conducción ↓↓</li>
              <li>Latencia distal/Pico ↑↑</li>
              <li>Amplitud normal o ↓ leve (por dispersión temporal)</li>
              <li>Ondas F prolongadas o ausentes</li>
              <li>Bloqueo de conducción / Dispersión temporal</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-blue-200 mb-1">Axonal</h4>
            <ul className="list-disc pl-5 text-blue-300/90 text-xs space-y-0.5">
              <li>Amplitud Potencial (CMAP/SNAP) ↓↓</li>
              <li>Velocidad de conducción normal o ↓ leve</li>
              <li>Latencia distal/Pico normal o ↑ leve</li>
              <li>Ondas F normales o ausentes si amplitud CMAP muy baja</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-blue-200 mb-1">Mixto</h4>
            <ul className="list-disc pl-5 text-blue-300/90 text-xs space-y-0.5">
              <li>Combinación de hallazgos axonales y desmielinizantes.</li>
              <li>Velocidad ↓, Amplitud ↓, Latencia ↑</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="flex justify-end mt-8">
        <button type="button" onClick={handleCompleteStudy}
          disabled={collectedResults.length === 0}
          className="flex items-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-base font-medium">
          <Save className="mr-2 h-5 w-5" /> Guardar y Continuar
        </button>
      </div>
    </div>
  );
};

export default NeuroConductionForm;